import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const auth = getAuth(app);

// Messaging - nur initialisieren wenn unterstützt
let messaging: any = null;
if (typeof window !== 'undefined') {
  isSupported().then((supported) => {
    if (supported) {
      messaging = getMessaging(app);
    }
  });
}

export { messaging };

// Anonymous Authentication für technischen Zugriff
export const authenticateAnonymously = async () => {
  try {
    const userCredential = await signInAnonymously(auth);
    console.log('Anonymous authentication successful:', userCredential.user.uid);
    return true;
  } catch (error) {
    console.error('Anonymous authentication failed:', error);
    return false;
  }
};

// Helper: Map team ID to backend topic format
export const getTopicForTeam = (teamId: string): string => {
  // team-1H → 1H, team-2H → 2H, etc.
  const shortId = teamId.replace('team-', '');
  return `results_${shortId}`;
};

// Push Notification Functions
export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    // Prüfe ob Messaging unterstützt wird
    const supported = await isSupported();
    if (!supported) {
      return null;
    }

    // Prüfe ob Berechtigung bereits erteilt wurde
    if (Notification.permission === 'granted') {
      const registration = await navigator.serviceWorker.ready;
      const currentMessaging = messaging || getMessaging(app);

      const token = await getToken(currentMessaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
      });

      return token;
    }

    // Frage nach Berechtigung
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      const registration = await navigator.serviceWorker.ready;
      const currentMessaging = messaging || getMessaging(app);

      const token = await getToken(currentMessaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
      });

      return token;
    }

    return null;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    throw error;
  }
};

// Subscribe to topic (via Firestore - backend processes it)
export const subscribeToTopic = async (token: string, topic: string): Promise<boolean> => {
  try {
    // Store subscription request in Firestore
    // Backend will pick it up and call Firebase Admin SDK's subscribeToTopic()
    const subscriptionId = `${token}_${topic}`;

    await setDoc(doc(db, 'topicSubscriptions', subscriptionId), {
      fcmToken: token,
      topic: topic,
      action: 'subscribe',
      createdAt: new Date(),
      processed: false
    });

    console.log(`Stored subscription request for topic: ${topic}`);
    return true;
  } catch (error) {
    console.error('Error subscribing to topic:', error);
    return false;
  }
};

// Unsubscribe from topic (via Firestore - backend processes it)
export const unsubscribeFromTopic = async (token: string, topic: string): Promise<boolean> => {
  try {
    // Store unsubscription request in Firestore
    const subscriptionId = `${token}_${topic}`;

    await setDoc(doc(db, 'topicSubscriptions', subscriptionId), {
      fcmToken: token,
      topic: topic,
      action: 'unsubscribe',
      createdAt: new Date(),
      processed: false
    });

    console.log(`Stored unsubscription request for topic: ${topic}`);
    return true;
  } catch (error) {
    console.error('Error unsubscribing from topic:', error);
    return false;
  }
};

// Foreground Message Listener
export const onMessageListener = () =>
  new Promise((resolve) => {
    if (messaging) {
      onMessage(messaging, (payload) => {
        console.log('Message received:', payload);
        resolve(payload);
      });
    }
  });